import { ChevronDown, ChevronUp, Lock, Scissors, Unlock } from 'lucide-react';
import type { UseImageEditorResult } from '../../hooks/useImageEditor';

interface CropperPanelProps {
  editor: UseImageEditorResult;
  /** 嵌在對話框／drawer 內時不顯示收合鈕，直接展開內容 */
  embeddedInPanel?: boolean;
  /** 原圖大小（不裁切）或自訂大小；僅在 embeddedInPanel 時使用 */
  cropSizeMode?: 'sameAsImage' | 'custom';
  setCropSizeMode?: (mode: 'sameAsImage' | 'custom') => void;
}

/**
 * 裁剪與預覽區塊：顯示目前圖片預覽、裁剪框與相關操作。
 */
export function CropperPanel({
  editor,
  embeddedInPanel = false,
  cropSizeMode = 'sameAsImage',
  setCropSizeMode,
}: CropperPanelProps) {
  const {
    cropSourceUrl,
    imgRef,
    minPx,
    file,
    originalSize,
    isCroppingMode,
    cropRect,
    setOriginalSize,
    setError,
    notifyCropSourceLoaded,
    handleToggleCropMode,
    handleResetCrop,
    handleCropMouseDown,
    handleCropWidthPxChange,
    handleCropHeightPxChange,
    toggleFreeCropAspectLock,
    cropFreeAspectRatioLocked,
    aspectRatios,
    aspectRatioId,
    setAspectRatioId,
    useSmartCrop,
    setUseSmartCrop,
  } = editor;

  if (!cropSourceUrl) {
    return (
      <span className="inline-flex items-center gap-1 text-sm font-medium text-gray-700 dark:text-gray-300">
        <Scissors className="h-4 w-4" />
        <span>裁切</span>
      </span>
    );
  }

  const showContent = embeddedInPanel || isCroppingMode;
  const showCustomCropUI =
    showContent && (!embeddedInPanel || cropSizeMode === 'custom');

  return (
    <div className="flex flex-col gap-3">
      {!embeddedInPanel && (
        <button
          type="button"
          onClick={handleToggleCropMode}
          disabled={!file || !cropSourceUrl}
          className="flex w-full items-center justify-between rounded py-1 text-left text-sm font-medium text-gray-700 dark:text-gray-300 disabled:opacity-50"
          aria-expanded={isCroppingMode}
        >
          <span className="inline-flex items-center gap-1">
            <Scissors className="h-4 w-4" />
            <span>裁切</span>
          </span>
          {isCroppingMode ? (
            <ChevronUp className="h-4 w-4 shrink-0" aria-hidden />
          ) : (
            <ChevronDown className="h-4 w-4 shrink-0" aria-hidden />
          )}
        </button>
      )}

      {embeddedInPanel && setCropSizeMode && (
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="cropSizeMode"
              checked={cropSizeMode === 'sameAsImage'}
              onChange={() => {
                setCropSizeMode('sameAsImage');
                handleResetCrop();
              }}
              className="rounded-full border-gray-300 text-primary"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              原圖大小
            </span>
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="radio"
              name="cropSizeMode"
              checked={cropSizeMode === 'custom'}
              onChange={() => setCropSizeMode('custom')}
              className="rounded-full border-gray-300 text-primary"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              自訂大小
            </span>
          </label>
        </div>
      )}

      {embeddedInPanel && cropSizeMode === 'sameAsImage' && (
        <>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            使用原圖大小，不裁切。
          </p>
        </>
      )}

      {showCustomCropUI && (
        <>
          <div className="flex h-[250px] max-w-full items-center justify-center overflow-hidden rounded border border-gray-200 dark:border-gray-600">
            <div
              className="relative max-h-full max-w-full shrink-0 overflow-hidden"
              style={{
                aspectRatio: originalSize
                  ? `${originalSize.width} / ${originalSize.height}`
                  : undefined,
              }}
            >
              <img
                key={cropSourceUrl ?? 'crop-src'}
                ref={imgRef}
                src={cropSourceUrl ?? undefined}
                alt="裁切來源（原圖）"
                className="h-full w-full object-contain"
                onLoad={() => {
                  setError(null);
                  if (imgRef.current) {
                    setOriginalSize({
                      width: imgRef.current.naturalWidth,
                      height: imgRef.current.naturalHeight,
                    });
                  }
                  notifyCropSourceLoaded?.();
                }}
                onError={() => setError('圖片載入失敗')}
              />
              {cropRect && !useSmartCrop && (
                <>
                  <div className="pointer-events-none absolute inset-0 bg-black/40" />
                  <div
                    className="absolute cursor-move border-2 border-primary bg-transparent"
                    style={{
                      left: `${cropRect.x * 100}%`,
                      top: `${cropRect.y * 100}%`,
                      width: `${cropRect.width * 100}%`,
                      height: `${cropRect.height * 100}%`,
                    }}
                    onMouseDown={event => handleCropMouseDown(event, 'move')}
                  >
                    <div
                      className="absolute -top-1.5 -left-1.5 h-3 w-3 cursor-nw-resize rounded-full border border-white bg-primary"
                      onMouseDown={event => handleCropMouseDown(event, 'nw')}
                    />
                    <div
                      className="absolute -top-1.5 -right-1.5 h-3 w-3 cursor-ne-resize rounded-full border border-white bg-primary"
                      onMouseDown={event => handleCropMouseDown(event, 'ne')}
                    />
                    <div
                      className="absolute -bottom-1.5 -left-1.5 h-3 w-3 cursor-sw-resize rounded-full border border-white bg-primary"
                      onMouseDown={event => handleCropMouseDown(event, 'sw')}
                    />
                    <div
                      className="absolute -bottom-1.5 -right-1.5 h-3 w-3 cursor-se-resize rounded-full border border-white bg-primary"
                      onMouseDown={event => handleCropMouseDown(event, 'se')}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="rounded border border-gray-200 bg-gray-50/30 p-2 dark:border-gray-600 dark:bg-gray-800/30">
            <div className="mb-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-400">
                長寬比
              </span>
            </div>
            <div className="flex flex-wrap justify-center gap-2">
              {aspectRatios.map(ratio => (
                <button
                  key={ratio.id}
                  type="button"
                  onClick={() => setAspectRatioId(ratio.id)}
                  className={`rounded px-2.5 py-1 text-xs ${
                    aspectRatioId === ratio.id
                      ? 'bg-primary text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
                >
                  {ratio.label}
                </button>
              ))}
            </div>
            {aspectRatioId !== 'original' && (
              <label className="mt-2 flex items-center justify-center gap-2">
                <input
                  type="checkbox"
                  checked={useSmartCrop}
                  onChange={event => setUseSmartCrop(event.target.checked)}
                  className="rounded border-gray-300"
                />
                <span className="text-xs text-gray-700 dark:text-gray-300">
                  智能裁切（依主體）
                </span>
              </label>
            )}
            {cropRect && (
              <div className="mt-3 flex flex-wrap items-center justify-center gap-3 border-t border-gray-200 pt-3 text-xs text-gray-600 dark:border-gray-600 dark:text-gray-400">
                <span>裁切尺寸：</span>
                {originalSize ? (
                  <>
                    <label className="flex items-center gap-1">
                      <span>寬</span>
                      <input
                        type="number"
                        min={minPx}
                        max={originalSize.width}
                        value={Math.round(cropRect.width * originalSize.width)}
                        onChange={handleCropWidthPxChange}
                        className="w-20 rounded border border-gray-300 bg-white px-1.5 py-1 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      />
                      <span>px</span>
                    </label>
                    <span className="text-gray-400">×</span>
                    {aspectRatioId === 'original' ? (
                      <button
                        type="button"
                        onClick={event => {
                          event.stopPropagation();
                          toggleFreeCropAspectLock();
                        }}
                        className={`inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border text-gray-600 transition-colors dark:text-gray-300 ${
                          cropFreeAspectRatioLocked
                            ? 'border-primary bg-primary/10 text-primary dark:bg-primary/20'
                            : 'border-gray-300 bg-white hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:hover:bg-gray-700'
                        }`}
                        aria-pressed={cropFreeAspectRatioLocked}
                        title={
                          cropFreeAspectRatioLocked
                            ? '已鎖定寬高比：拖曳角落會等比例縮放'
                            : '鎖定目前裁切寬高比'
                        }
                        aria-label={
                          cropFreeAspectRatioLocked
                            ? '解除鎖定裁切寬高比'
                            : '鎖定裁切寬高比'
                        }
                      >
                        {cropFreeAspectRatioLocked ? (
                          <Lock className="h-4 w-4" aria-hidden />
                        ) : (
                          <Unlock className="h-4 w-4" aria-hidden />
                        )}
                      </button>
                    ) : null}
                    <label className="flex items-center gap-1">
                      <span>高</span>
                      <input
                        type="number"
                        min={minPx}
                        max={originalSize.height}
                        value={Math.round(
                          cropRect.height * originalSize.height
                        )}
                        onChange={handleCropHeightPxChange}
                        className="w-20 rounded border border-gray-300 bg-white px-1.5 py-1 text-xs text-gray-900 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
                      />
                      <span>px</span>
                    </label>
                  </>
                ) : (
                  <span className="text-gray-400">載入中…</span>
                )}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
